import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getBookingByReference } from '../lib/stripeBooking';
import { formatCurrency } from '../lib/bookingUtils';

const Success: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const bookingRef = searchParams.get('booking_ref');
  const state = location.state as any;
  const isCancellation = state?.type === 'cancellation';
  const isReschedule = state?.type === 'reschedule';
  const cancellationAppointment = state?.appointment;
  const rescheduleAppointment = state?.appointment;

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  useEffect(() => {
    if (bookingRef && !isCancellation) {
      loadBookingDetails();
    } else {
      setLoading(false);
    }
  }, [bookingRef, isCancellation]);

  const loadBookingDetails = async () => {
    try {
      const bookingData = await getBookingByReference(bookingRef!);
      setBooking(bookingData);
    } catch (err) {
      console.error('Failed to load booking details:', err);
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const content = {
    en: {
      title: "Booking Confirmed!",
      cancellationTitle: "Cancellation Confirmed!",
      rescheduleTitle: "Reschedule Confirmed!",
      subtitle: "Thank you for your booking",
      cancellationSubtitle: "Your appointment has been cancelled",
      rescheduleSubtitle: "Your appointment has been rescheduled",
      description: "Your appointment has been successfully booked and payment confirmed. You will receive a confirmation email shortly.",
      cancellationDescription: "Your appointment has been successfully cancelled. You will receive a confirmation email shortly.",
      rescheduleDescription: "Your appointment has been successfully rescheduled. You will receive a confirmation email shortly.",
      bookingDetails: "Booking Details",
      cancellationDetails: "Cancelled Appointment Details",
      rescheduleDetails: "Rescheduled Appointment Details",
      reference: "Booking Reference",
      service: "Service",
      date: "Date",
      time: "Time",
      amount: "Amount Paid",
      backToHome: "Back to Home",
      redirectMessage: "You will be redirected to the homepage in 10 seconds...",
      loadingBooking: "Loading booking details...",
      bookingError: "Unable to load booking details"
    },
    da: {
      title: "Booking Bekræftet!",
      cancellationTitle: "Aflysning Bekræftet!",
      rescheduleTitle: "Ombookning Bekræftet!",
      subtitle: "Tak for din booking",
      cancellationSubtitle: "Din aftale er blevet aflyst",
      rescheduleSubtitle: "Din aftale er blevet ombooked",
      description: "Din aftale er blevet booket og betalingen er bekræftet. Du vil modtage en bekræftelsesmail snarest.",
      cancellationDescription: "Din aftale er blevet aflyst. Du vil modtage en bekræftelsesmail snarest.",
      rescheduleDescription: "Din aftale er blevet ombooked. Du vil modtage en bekræftelsesmail snarest.",
      bookingDetails: "Booking Detaljer",
      cancellationDetails: "Aflyste Aftale Detaljer",
      rescheduleDetails: "Ombookede Aftale Detaljer",
      reference: "Booking Reference",
      service: "Service",
      date: "Dato",
      time: "Tid",
      amount: "Betalt Beløb",
      backToHome: "Tilbage til Forsiden",
      redirectMessage: "Du vil blive omdirigeret til forsiden om 10 sekunder...",
      loadingBooking: "Indlæser booking detaljer...",
      bookingError: "Kan ikke indlæse booking detaljer"
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#A68B7C]/20 to-transparent opacity-50" />
        
        <div className="relative z-10">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            {isCancellation ? (
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : isReschedule ? (
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>

          <h1 
            className="text-3xl font-serif font-bold mb-2 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {isCancellation 
              ? content[language as keyof typeof content].cancellationTitle 
              : isReschedule
              ? content[language as keyof typeof content].rescheduleTitle
              : content[language as keyof typeof content].title}
          </h1>
          
          <h2 className="text-xl text-[#A68B7C] mb-4">
            {isCancellation 
              ? content[language as keyof typeof content].cancellationSubtitle 
              : isReschedule
              ? content[language as keyof typeof content].rescheduleSubtitle
              : content[language as keyof typeof content].subtitle}
          </h2>
          
          <p className="text-gray-300 mb-8">
            {isCancellation 
              ? content[language as keyof typeof content].cancellationDescription 
              : isReschedule
              ? content[language as keyof typeof content].rescheduleDescription
              : content[language as keyof typeof content].description}
          </p>

          {/* Booking Details */}
          {loading && bookingRef && !isCancellation && (
            <div className="mb-6 p-4 bg-white/5 rounded-lg">
              <p className="text-gray-300">{content[language as keyof typeof content].loadingBooking}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400">{content[language as keyof typeof content].bookingError}</p>
            </div>
          )}

          {booking && !isCancellation && (
            <div className="mb-6 p-4 bg-white/5 rounded-lg text-left">
              <h3 className="text-lg font-semibold text-white mb-3">
                {content[language as keyof typeof content].bookingDetails}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].reference}:</span>
                  <span className="text-white font-mono">{booking.booking_reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].service}:</span>
                  <span className="text-white">{booking.service_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].date}:</span>
                  <span className="text-white">{booking.appointment_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].time}:</span>
                  <span className="text-white">{booking.appointment_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].amount}:</span>
                  <span className="text-white">{formatCurrency(booking.payment_amount)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Cancellation Details */}
          {(isCancellation || isReschedule) && (cancellationAppointment || rescheduleAppointment) && (
            <div className="mb-6 p-4 bg-white/5 rounded-lg text-left">
              <h3 className="text-lg font-semibold text-white mb-3">
                {isCancellation 
                  ? content[language as keyof typeof content].cancellationDetails
                  : content[language as keyof typeof content].rescheduleDetails}
              </h3>
              <div className="space-y-2 text-sm">
                {isReschedule && rescheduleAppointment && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Old Date:</span>
                      <span className="text-white">{rescheduleAppointment.old_date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Old Time:</span>
                      <span className="text-white">{rescheduleAppointment.old_time}</span>
                    </div>
                    <div className="h-px bg-white/10 my-2"></div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">New Date:</span>
                      <span className="text-white">{rescheduleAppointment.new_date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">New Time:</span>
                      <span className="text-white">{rescheduleAppointment.new_time}</span>
                    </div>
                  </>
                )}
                {isCancellation && cancellationAppointment && (
                  <>
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].reference}:</span>
                  <span className="text-white font-mono">{cancellationAppointment.booking_reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].service}:</span>
                  <span className="text-white">{cancellationAppointment.service_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].date}:</span>
                  <span className="text-white">{cancellationAppointment.appointment_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{content[language as keyof typeof content].time}:</span>
                  <span className="text-white">{cancellationAppointment.appointment_time}</span>
                </div>
                  </>
                )}
              </div>
            </div>
          )}

          <Link
            to="/"
            className="inline-block px-6 py-3 bg-[#A68B7C] text-white rounded-lg hover:bg-[#95796A] transition-all duration-300 mb-4"
          >
            {content[language as keyof typeof content].backToHome}
          </Link>

          <p className="text-sm text-gray-400">
            {content[language as keyof typeof content].redirectMessage}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Success;